import React, { useState } from "react";
import { Clock, ChevronDown, Lightbulb, Package, Home, Sparkles } from "lucide-react";
import { projectFunFacts } from "../electronicsKitData";

const ProjectCard = ({ project, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const funFact = projectFunFacts[project.id];

  return (
    <div
      className={`project-card ${isOpen ? "is-expanded" : ""}`}
      id={`project-${project.id}`}
    >
      {/* Top Banner Bar */}
      <div className="project-card-header">
        <div className="number-category-group">
          <span className="project-number">#{project.number}</span>
          <span className="project-category-badge">{project.category}</span>
          <span className={`project-difficulty-badge diff-${project.difficulty.toLowerCase()}`}>
            {project.difficulty === "Beginner"
              ? "🟢 Starter Spark"
              : project.difficulty === "Intermediate"
              ? "🟡 Explorer Lab"
              : "🟣 Master Challenge"}
          </span>
        </div>
        <div className="project-time-badge">
          <Clock size={14} />
          <span>{project.time}</span>
        </div>
      </div>

      {/* Title & Tagline */}
      <div className="project-title-area">
        <h3 className="project-title">{project.title}</h3>
        <p className="project-tagline">{project.tagline}</p>
      </div>

      <p className="project-desc">{project.description}</p>

      {/* Materials Summary Pills */}
      <div className="project-needs-box">
        <div className="needs-group">
          <span className="needs-label">
            <Package size={14} className="inline mr-1 text-blue-500" /> Kit:
          </span>
          <div className="needs-pills">
            {project.kitNeeds.map((item, idx) => (
              <span key={idx} className="need-pill kit-pill">
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="needs-group">
          <span className="needs-label">
            <Home size={14} className="inline mr-1 text-amber-500" /> Home:
          </span>
          <div className="needs-pills">
            {project.homeNeeds.map((item, idx) => (
              <span key={idx} className="need-pill home-pill">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Anchors */}
      {project.id === 7 && (
        <div className="interactive-shortcut-box">
          <Sparkles size={16} className="text-amber-500" />
          <span>Includes Interactive Morse Code Telegraph Station below!</span>
          <a href="#morse-widget" className="shortcut-link">
            Open Telegraph →
          </a>
        </div>
      )}

      {project.id === 11 && (
        <div className="interactive-shortcut-box">
          <Sparkles size={16} className="text-blue-500" />
          <span>Includes Interactive Conductor Detective Test Bench below!</span>
          <a href="#conductor-widget" className="shortcut-link">
            Launch Detective Game →
          </a>
        </div>
      )}

      {/* Expandable Step-by-Step Instructions with Smooth Collapse Animation */}
      <div
        className={`project-steps-collapse-wrapper ${isOpen ? "is-open" : ""}`}
        aria-hidden={!isOpen}
      >
        <div className="project-steps-collapse-inner">
          <div className="project-steps-container">
            {/* Visual "You Need" diagram from PDF shown when expanded */}
            {project.youNeedImage && (
              <div className="pdf-you-need-container mb-5">
                <img
                  src={project.youNeedImage}
                  alt={`What you need for ${project.title}`}
                  className="w-full h-auto rounded-xl object-contain shadow-sm border border-slate-100"
                  loading="lazy"
                />
              </div>
            )}

            <h4 className="steps-heading">Step-by-Step Assembly:</h4>
            <div className="steps-list">
              {project.steps.map((st) => (
                <div key={st.stepNumber} className="step-item-card">
                  {st.image ? (
                    <div className="step-image-wrapper">
                      <img
                        src={st.image}
                        alt={`Step ${st.stepNumber}: ${st.instruction}`}
                        className="w-full h-auto rounded-lg object-contain"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="step-card-header">
                        <span className="step-pill">STEP {st.stepNumber}</span>
                        <h5 className="step-title">{st.title}</h5>
                      </div>
                      <p className="step-instruction">{st.instruction}</p>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Try It Out Success Diagram */}
            {project.tryItImage && (
              <div className="try-it-container">
                <div className="try-it-image-wrapper">
                  <img
                    src={project.tryItImage}
                    alt={`Testing circuit for ${project.title}`}
                    className="w-full h-auto rounded-lg object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            )}

            {/* Alternative 9V / No Battery Card if applicable */}
            {project.noBatteryImage && (
              <div className="alternative-card">
                <div className="alternative-image-wrapper">
                  <img
                    src={project.noBatteryImage}
                    alt="No coin battery alternative with 9V"
                    className="w-full h-auto rounded-lg object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            )}

            {/* Maker Tip Box */}
            {project.proTip && (
              <div className="project-protip-box">
                <Lightbulb size={18} className="protip-icon" />
                <div>
                  <strong>Maker Tip:</strong> {project.proTip}
                </div>
              </div>
            )}

            {/* Kid Science Fun Fact Box */}
            {funFact && (
              <div className="project-fun-fact-box">
                <div className="fun-fact-badge">
                  <Sparkles size={14} /> 🚀 Junior Inventor Fun Fact:
                </div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Bar at the Bottom: View Steps Button */}
      <div className="card-action-bar">
        <button
          onClick={() => {
            const cardEl = document.getElementById(`project-${project.id}`);
            if (isOpen && cardEl) {
              cardEl.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            setIsOpen(!isOpen);
          }}
          className="btn-toggle-steps"
          aria-expanded={isOpen}
        >
          <span>{isOpen ? "Hide Assembly Steps" : `View Step-by-Step Guide (${project.steps.length} Steps)`}</span>
          <ChevronDown
            size={18}
            className={`toggle-chevron ${isOpen ? "is-rotated" : ""}`}
          />
        </button>
      </div>
    </div>
  );
};

export default ProjectCard;